import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import ProductDetailModal from '../../components/ui/ProductDetailModal';
import SummaryCard from './components/SummaryCard';
import FilterPanel from './components/FilterPanel';
import ResultsTable from './components/ResultsTable';
import TaxDistributionChart from './components/TaxDistributionChart';
import ExportPanel from './components/ExportPanel';
import ComparisonPanel from './components/ComparisonPanel';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import {
  TaxCalculation,
  ConsultationSummary,
  FilterOptions,
  ExportOptions,
  ChartData
} from './types';

const TaxCalculationResults = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<TaxCalculation | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [sortField, setSortField] = useState<keyof TaxCalculation>('productCode');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [filters, setFilters] = useState<FilterOptions>({
    taxType: 'all',
    rateRange: { min: 0, max: 100 },
    status: 'all',
    searchTerm: '',
    ufFilter: ''
  });

  const mockSummary: ConsultationSummary = {
    consultationId: 'CONS-2024-015',
    totalProducts: 42,
    totalTaxAmount: 125847.32,
    averageEffectiveRate: 18.75,
    complianceStatus: 'compliant',
    calculatedAt: new Date('2024-01-20T14:30:00'),
    apiCallDuration: 1247,
    companyName: 'Indústria Brasileira de Componentes Ltda',
    cnpj: '12.345.678/0001-90'
  };

  const mockCalculations: TaxCalculation[] = [
    {
      id: '1',
      productCode: 'PROD-001',
      productName: 'Componente Eletrônico Tipo A',
      ncm: '8542.31.90',
      cest: '01.001.00',
      icms: 1250.00,
      ipi: 450.00,
      pis: 165.00,
      cofins: 760.00,
      totalTax: 2625.00,
      effectiveRate: 18.5,
      cst: '000',
      cfop: '5102',
      ufOrigin: 'SP',
      ufDestination: 'RJ',
      exemptions: [],
      status: 'compliant',
      calculatedAt: new Date('2024-01-20T14:30:00'),
      apiResponseTime: 245
    },
    {
      id: '2',
      productCode: 'PROD-002',
      productName: 'Resistor de Precisão 1K Ohm',
      ncm: '8533.21.00',
      cest: '01.002.00',
      icms: 890.00,
      ipi: 320.00,
      pis: 118.00,
      cofins: 545.00,
      totalTax: 1873.00,
      effectiveRate: 16.2,
      cst: '000',
      cfop: '5102',
      ufOrigin: 'SP',
      ufDestination: 'MG',
      exemptions: ['Redução de base de cálculo ICMS - Conv. 52/91'],
      status: 'compliant',
      calculatedAt: new Date('2024-01-20T14:30:00'),
      apiResponseTime: 198
    },
    {
      id: '3',
      productCode: 'PROD-003',
      productName: 'Capacitor Eletrolítico 100uF',
      ncm: '8532.22.00',
      cest: '01.003.00',
      icms: 1450.00,
      ipi: 520.00,
      pis: 190.00,
      cofins: 875.00,
      totalTax: 3035.00,
      effectiveRate: 19.8,
      cst: '000',
      cfop: '5102',
      ufOrigin: 'SP',
      ufDestination: 'RS',
      exemptions: [],
      status: 'warning',
      calculatedAt: new Date('2024-01-20T14:30:00'),
      apiResponseTime: 312
    },
    {
      id: '4',
      productCode: 'PROD-004',
      productName: 'Transistor NPN BC547',
      ncm: '8541.21.00',
      cest: '01.004.00',
      icms: 675.00,
      ipi: 240.00,
      pis: 88.00,
      cofins: 405.00,
      totalTax: 1408.00,
      effectiveRate: 15.3,
      cst: '000',
      cfop: '5102',
      ufOrigin: 'SP',
      ufDestination: 'PR',
      exemptions: [],
      status: 'compliant',
      calculatedAt: new Date('2024-01-20T14:30:00'),
      apiResponseTime: 223
    },
    {
      id: '5',
      productCode: 'PROD-005',
      productName: 'Diodo Retificador 1N4007',
      ncm: '8541.10.12',
      cest: '01.005.00',
      icms: 520.00,
      ipi: 185.00,
      pis: 68.00,
      cofins: 313.00,
      totalTax: 1086.00,
      effectiveRate: 14.1,
      cst: '000',
      cfop: '5102',
      ufOrigin: 'SP',
      ufDestination: 'SC',
      exemptions: ['Isenção IPI - Decreto 8.950/2016'],
      status: 'compliant',
      calculatedAt: new Date('2024-01-20T14:30:00'),
      apiResponseTime: 267
    }
  ];

  const [calculations, setCalculations] = useState<TaxCalculation[]>(mockCalculations);
  const [filteredCalculations, setFilteredCalculations] = useState<TaxCalculation[]>(mockCalculations);

  const taxTypeChartData: ChartData[] = [
    { label: 'ICMS', value: 52850.00, color: 'var(--color-primary)' },
    { label: 'IPI', value: 18920.00, color: 'var(--color-accent)' },
    { label: 'PIS', value: 6935.00, color: 'var(--color-success)' },
    { label: 'COFINS', value: 31942.00, color: 'var(--color-warning)' }
  ];

  const ufDistributionData: ChartData[] = [
    { label: 'São Paulo', value: 45230.00, color: 'var(--color-primary)' },
    { label: 'Rio de Janeiro', value: 32150.00, color: 'var(--color-accent)' },
    { label: 'Minas Gerais', value: 28467.00, color: 'var(--color-success)' },
    { label: 'Rio Grande do Sul', value: 20000.00, color: 'var(--color-warning)' }
  ];

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.key === 'e') {
        e.preventDefault();
        handleExport({ format: 'excel', includeColumns: [], filterApplied: false });
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  useEffect(() => {
    let filtered = [...calculations];

    if (filters.searchTerm) {
      filtered = filtered.filter(
        calc =>
          calc.productCode.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
          calc.productName.toLowerCase().includes(filters.searchTerm.toLowerCase())
      );
    }

    if (filters.taxType !== 'all') {
      filtered = filtered.filter(calc => {
        switch (filters.taxType) {
          case 'icms':
            return calc.icms > 0;
          case 'ipi':
            return calc.ipi > 0;
          case 'pis':
            return calc.pis > 0;
          case 'cofins':
            return calc.cofins > 0;
          default:
            return true;
        }
      });
    }

    if (filters.status !== 'all') {
      filtered = filtered.filter(calc => calc.status === filters.status);
    }

    if (filters.ufFilter) {
      filtered = filtered.filter(
        calc => calc.ufOrigin === filters.ufFilter || calc.ufDestination === filters.ufFilter
      );
    }

    filtered = filtered.filter(
      calc =>
        calc.effectiveRate >= filters.rateRange.min &&
        calc.effectiveRate <= filters.rateRange.max
    );

    setFilteredCalculations(filtered);
  }, [filters, calculations]);

  const handleSort = (field: keyof TaxCalculation) => {
    const newDirection = sortField === field && sortDirection === 'asc' ? 'desc' : 'asc';
    setSortField(field);
    setSortDirection(newDirection);

    const sorted = [...filteredCalculations].sort((a, b) => {
      const aValue = a[field];
      const bValue = b[field];

      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return newDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return newDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    setFilteredCalculations(sorted);
  };

  const handleProductClick = (calculation: TaxCalculation) => {
    setSelectedProduct(calculation);
    setIsModalOpen(true);
  };

  const handleExport = (options: ExportOptions) => {
    console.log('Exportando com opções:', options);
    alert(`Exportando ${options.filterApplied ? filteredCalculations.length : calculations.length} registros em formato ${options.format.toUpperCase()}`);
  };

  const handleCompare = (previousConsultationId: string) => {
    console.log('Comparando com consulta:', previousConsultationId);
  };

  const handleResetFilters = () => {
    setFilters({
      taxType: 'all',
      rateRange: { min: 0, max: 100 },
      status: 'all',
      searchTerm: '',
      ufFilter: ''
    });
  };

  const productDetailForModal = selectedProduct
    ? {
        id: selectedProduct.id,
        name: selectedProduct.productName,
        code: selectedProduct.productCode,
        category: 'Componentes Eletrônicos',
        taxRate: selectedProduct.effectiveRate,
        description: `Produto classificado sob NCM ${selectedProduct.ncm} com CEST ${selectedProduct.cest}. Aplicável para operações interestaduais com CFOP ${selectedProduct.cfop}.`,
        regulations: [
          `CST ${selectedProduct.cst} - Tributação normal do ICMS`,
          `CFOP ${selectedProduct.cfop} - Venda de produção do estabelecimento`,
          'Sujeito à substituição tributária conforme legislação estadual'
        ],
        exemptions: selectedProduct.exemptions,
        calculationMethod: `Base de cálculo: Valor da operação\nICMS: ${selectedProduct.icms.toFixed(2)} (alíquota aplicada)\nIPI: ${selectedProduct.ipi.toFixed(2)} (alíquota aplicada)\nPIS: ${selectedProduct.pis.toFixed(2)} (regime não-cumulativo)\nCOFINS: ${selectedProduct.cofins.toFixed(2)} (regime não-cumulativo)`
      }
    : null;

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onCollapsedChange={setSidebarCollapsed} />
      
      <div className={`transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        <Header 
          title="Resultados da Consulta Tributária" 
          onUserMenuClick={() => {}} 
        />
        
        <main className="pt-16 p-6">
          <div className="max-w-[1600px] mx-auto space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">
                  Análise de Resultados Tributários
                </h1>
                <p className="text-muted-foreground">
                  Consulta processada com sucesso • {filteredCalculations.length} de {calculations.length} produtos exibidos
                </p>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/consultation-history')}
                  iconName="History"
                  iconPosition="left"
                >
                  Ver Histórico
                </Button>
                <Button
                  variant="default"
                  onClick={() => navigate('/new-tax-consultation-form')}
                  iconName="Plus"
                  iconPosition="left"
                >
                  Nova Consulta
                </Button>
              </div>
            </div>

            <SummaryCard summary={mockSummary} />

            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold text-foreground">Detalhamento por Produto</h2>
              <ExportPanel
                onExport={handleExport}
                totalRecords={calculations.length}
                filteredRecords={filteredCalculations.length}
              />
            </div>

            <FilterPanel
              filters={filters}
              onFilterChange={setFilters}
              onReset={handleResetFilters}
            />

            <ResultsTable
              calculations={filteredCalculations}
              onProductClick={handleProductClick}
              onSort={handleSort}
              sortField={sortField}
              sortDirection={sortDirection}
            />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <TaxDistributionChart
                data={taxTypeChartData}
                title="Distribuição por Tipo de Imposto"
                type="bar"
              />
              <TaxDistributionChart
                data={ufDistributionData}
                title="Distribuição por UF de Destino"
                type="pie"
              />
            </div>

            <ComparisonPanel
              currentConsultationId={mockSummary.consultationId}
              onCompare={handleCompare}
            />

            <div className="bg-card rounded-lg border border-border p-6 shadow-sm">
              <div className="flex items-start gap-3">
                <Icon name="Info" size={20} color="var(--color-accent)" className="mt-0.5 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-2">
                    Informações sobre os Cálculos
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    Os valores apresentados foram calculados com base nas informações fornecidas e na legislação tributária vigente. 
                    Para produtos com isenções ou reduções de base de cálculo, os benefícios foram aplicados automaticamente. 
                    Recomenda-se validação adicional para operações específicas ou casos excepcionais.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      <ProductDetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        product={productDetailForModal}
      />
    </div>
  );
};

export default TaxCalculationResults;